/**
 * Hook to fetch the authenticated member's upcoming shifts.
 * Used on the Home page to show upcoming shift cards.
 */

import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

export interface UpcomingShift {
  id: number;
  date: string;
  shift_start: string;
  shift_end: string;
  is_weekend: boolean;
  is_holiday: boolean;
  is_override: boolean;
  notes: string | null;
}

interface UpcomingShiftsResponse {
  shifts: UpcomingShift[];
  battalion: { id: number; name: string; number: number } | null;
}

export interface UseMyUpcomingShiftsReturn {
  shifts: UpcomingShift[];
  battalion: { id: number; name: string; number: number } | null;
  loading: boolean;
  error: string | null;
}

export function useMyUpcomingShifts(): UseMyUpcomingShiftsReturn {
  const [shifts, setShifts] = useState<UpcomingShift[]>([]);
  const [battalion, setBattalion] = useState<{ id: number; name: string; number: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchUpcoming() {
      try {
        const data = await apiFetch<UpcomingShiftsResponse>('/api/schedule/my-upcoming?limit=5');
        if (!cancelled) {
          setShifts(data.shifts);
          setBattalion(data.battalion);
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load upcoming shifts');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchUpcoming();
    return () => {
      cancelled = true;
    };
  }, []);

  return { shifts, battalion, loading, error };
}
